import { BrowserApi, PushTokenApi } from '@/openapi-clients-generated/auth';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, type Messaging } from 'firebase/messaging';
import { Logger } from './logger';
import { serviceWorkerManager } from './sw.manager';

const logger = Logger.createScoped('FCM');

let app: FirebaseApp | undefined;
let messaging: Messaging | undefined;

export const FCM = {
    init() {
        if (!('serviceWorker' in navigator)) {
            return logger.warn('Service Workers are not supported in this browser.');
        }

        app = initializeApp({
            apiKey: 'AIzaSyDH76XLlZ5-dHfrva_pERCDTgYHe5kr9ao',
            authDomain: 'zynosuite.firebaseapp.com',
            projectId: 'zynosuite',
            messagingSenderId: '603717425639',
            appId: '1:603717425639:web:ff1ea2bfdd399c2f980542'
        });
        messaging = getMessaging(app);
        onMessage(messaging, async payload => {
            logger.info('FCM foreground message received', payload);
            serviceWorkerManager.postMessage('notificationReceived', payload);
        });
    },

    refreshNotificationRegistration() {
        if (!('Notification' in window)) return;
        if (Notification.permission === 'granted' || location.hash.includes('fcminit')) {
            this.registerNotifications();
        }
    },

    async registerNotifications() {
        if (!messaging) {
            logger.warn('Firebase Messaging is not initialized.');
            return false;
        }

        try {
            if (Notification.permission !== 'granted') {
                const result = await Notification.requestPermission();
                if (result !== 'granted') return false;
            }

            const pushKey = await this.getPushKey();
            // Store pushKey in Service Worker KV so it is available there
            this.storePushKeyInServiceWorker(pushKey).catch(err => logger.warn('Failed to store pushKey in SW', err));

            if (!serviceWorkerManager.registration) return logger.warn('Service Worker is not registered.');

            const token = await getToken(messaging, {
                serviceWorkerRegistration: serviceWorkerManager.registration,
                vapidKey: import.meta.env.VITE_APP_FCM_VAPID_KEY
            });

            await PushTokenApi.postPushTokenRegisterPushToken({
                body: {
                    token,
                    key: pushKey
                }
            });

            logger.info('FCM registration complete');
            return true;
        } catch (err) {
            logger.error('Error during FCM registration:', err);
        }
    },

    async getPushKey() {
        let pushKey = localStorage.getItem('zs:pushKey');
        if (!pushKey) {
            const key = await crypto.subtle.generateKey(
                {
                    name: 'AES-GCM',
                    length: 256
                },
                true,
                ['encrypt', 'decrypt']
            );
            const exportedKey = await crypto.subtle.exportKey('raw', key);
            pushKey = btoa(String.fromCharCode(...new Uint8Array(exportedKey)));
            localStorage.setItem('zs:pushKey', pushKey);
        }
        return pushKey;
    },

    async storePushKeyInServiceWorker(pushKey: string) {
        try {
            serviceWorkerManager.postMessage('fcmPushKeySet', { pushKey });
        } catch (err) {
            logger.warn('Error posting pushKey to SW', err);
        }
    },

    async ackNotifications(notificationIds: string[]) {
        for (const notificationId of notificationIds) {
            await BrowserApi.postBrowserAckNotification({
                path: { notificationId }
            });
        }
    }
};

(() => FCM.init())();
